// TestimonialsSection.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography, Paper, Stack, IconButton } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';

const TestimonialsSection = () => {
  const GREEN = '#43934A';
  const ORANGE = '#ff7f00';

  const bgImage = 'https://qleen.bold-themes.com/demo-01/wp-content/uploads/sites/2/2025/07/image_02.jpg';
  const bubble1 = 'https://qleen.bold-themes.com/demo-01/wp-content/uploads/sites/2/2025/07/floating_image_02.png';
  const bubble2 = 'https://qleen.bold-themes.com/demo-01/wp-content/uploads/sites/2/2025/07/floating_image_03.png';

const testimonials = useMemo(
  () => [
    {
      text: 'We had severe water damage after a pipe burst, and their team responded immediately. The drying and restoration work was thorough, professional, and stress-free.',
      name: 'Annie Bennedict',
      role: 'Homeowner',
      avatar:
        'https://qleen.bold-themes.com/demo-01/wp-content/uploads/sites/2/2019/02/testimonials_02.jpg',
    },
    {
      text: 'From water extraction to mold prevention, everything was handled perfectly. They explained each step clearly and completed the job faster than expected.',
      name: 'Pete Goldner',
      role: 'Property Manager',
      avatar:
        'https://qleen.bold-themes.com/demo-01/wp-content/uploads/sites/2/2025/07/testimonials_04.jpg',
    },
    {
      text: 'After sewage backup in our basement, their crew arrived the same day and restored the area safely. Extremely reliable, knowledgeable, and respectful team.',
      name: 'Rebecca Hawland',
      role: 'Business Owner',
      avatar:
        'https://qleen.bold-themes.com/demo-01/wp-content/uploads/sites/2/2019/02/testimonials_01.jpg',
    },
  ],
  []
);


  // simple auto-rotate (shows 2 cards on desktop, 1 on mobile)
  const [index, setIndex] = useState(0);
  const isPausedRef = useRef(false);

  useEffect(() => {
    const t = setInterval(() => {
      if (isPausedRef.current) return;
      setIndex((p) => (p + 1) % testimonials.length);
    }, 3000);
    return () => clearInterval(t);
  }, [testimonials.length]);

  const visible = useMemo(() => {
    const a = testimonials[index % testimonials.length];
    const b = testimonials[(index + 1) % testimonials.length];
    return [a, b];
  }, [index, testimonials]);

  const TestimonialCard = ({ t }) => (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'rgba(255,255,255,0.92)',
        borderRadius: 4,
        p: { xs: 3, md: 4 },
        minHeight: 220,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backdropFilter: 'blur(6px)'
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 46,
            lineHeight: 1,
            fontWeight: 900,
            color: ORANGE,
            mb: 1
          }}
        >
          “
        </Typography>

        <Typography sx={{ color: 'rgba(0,0,0,0.72)', lineHeight: 1.7 }}>
          {t.text}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
        <Box
          component="img"
          src={t.avatar}
          alt={t.name}
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            objectFit: 'cover'
          }}
        />
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{t.name}</Typography>
          <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>{t.role}</Typography>
        </Box>
      </Stack>
    </Paper>
  );

  const Dot = ({ active, onClick }) => (
    <Box
      onClick={onClick}
      role="button"
      aria-label="testimonial dot"
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.35)',
        cursor: 'pointer'
      }}
    />
  );

  return (
    <Box id="testimonials" sx={{ py: { xs: 7, md: 9 }, bgcolor: '#fff' }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            borderRadius: { xs: 6, md: 10 },
            overflow: 'hidden',
            bgcolor: GREEN
          }}
        >
          {/* Right image */}
          <Box
            sx={{
              position: 'absolute',
              top: 18,
              right: 18,
              bottom: 18,
              width: { xs: '100%', md: '50%' },
              borderRadius: 8,
              overflow: 'hidden',
              display: { xs: 'none', md: 'block' }
            }}
          >
            <Box
              component="img"
              src={bgImage}
              alt="testimonial"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>

          {/* floating bubbles */}
          <Box
            component="img"
            src={bubble1}
            alt=""
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'absolute',
              left: -30,
              bottom: -20,
              width: 150,
              pointerEvents: 'none',
              userSelect: 'none',
              opacity: 0.95
            }}
          />
          <Box
            component="img"
            src={bubble2}
            alt=""
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'absolute',
              left: '46%',
              top: -18,
              width: 150,
              pointerEvents: 'none',
              userSelect: 'none',
              opacity: 0.95
            }}
          />

          <Grid container>
            {/* Left content */}
            <Grid item xs={12} md={6} sx={{ position: 'relative', zIndex: 2 }}>
              <Box sx={{ p: { xs: 4, md: 7 } }}>
                <Typography
                  sx={{
                    fontFamily: '"Caveat", cursive',
                    color: 'rgba(255,255,255,0.95)',
                    fontSize: 22,
                    fontWeight: 700,
                    mb: 1
                  }}
                >
                  Testimonials
                </Typography>

                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 900,
                    letterSpacing: '-0.03em',
                    fontSize: { xs: 34, md: 44 },
                    lineHeight: 1.05
                  }}
                >
                  What Our Clients Think
                </Typography>

                {/* Google rating pill */}
                <Box
                  sx={{
                    mt: 2.5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.2,
                    py: 0.7,
                    borderRadius: 999,
                    bgcolor: 'rgba(0,0,0,0.12)'
                  }}
                >
                  <Box
                    component="img"
                    src="https://cdn.trustindex.io/assets/platform/Google/icon.svg"
                    alt="Google"
                    sx={{ width: 16, height: 16 }}
                  />
                  <Stack direction="row" spacing={0.2} alignItems="center">
                    {[1, 2, 3, 4].map((i) => (
                      <StarIcon key={i} sx={{ color: '#FFC107', fontSize: 16 }} />
                    ))}
                    <StarIcon sx={{ color: '#FFC107', fontSize: 16, opacity: 0.6 }} />
                  </Stack>
                  <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 12 }}>
                    4.7
                  </Typography>
                </Box>

                {/* Slider area */}
                <Box
                  sx={{ mt: 4 }}
                  onMouseEnter={() => {
                    isPausedRef.current = true;
                  }}
                  onMouseLeave={() => {
                    isPausedRef.current = false;
                  }}
                >
                  <Grid container spacing={3}>
                    {/* desktop: two cards */}
                    <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                      <TestimonialCard t={visible[0]} />
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                      <TestimonialCard t={visible[1]} />
                    </Grid>

                    {/* mobile: one card */}
                    <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
                      <TestimonialCard t={testimonials[index]} />
                    </Grid>
                  </Grid>

                  {/* dots */}
                  <Stack direction="row" spacing={1} sx={{ mt: 2.5, pl: 1 }}>
                    {testimonials.map((_, i) => (
                      <Dot
                        key={i}
                        active={i === index}
                        onClick={() => setIndex(i)}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Grid>

            {/* Right side placeholder on mobile (image shown as block below via background) */}
            <Grid item xs={12} md={6} sx={{ display: { xs: 'block', md: 'none' } }}>
              <Box sx={{ px: 4, pb: 4 }}>
                <Box
                  component="img"
                  src={bgImage}
                  alt="testimonial"
                  sx={{
                    width: '100%',
                    height: 260,
                    objectFit: 'cover',
                    borderRadius: 6
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default TestimonialsSection;
